import { useRef } from 'react';
import { addMilliseconds, differenceInMilliseconds } from 'date-fns';

import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from 'helpers/sessionStorage';

interface StorageData {
  ts: Date | null;
  isExecuted: boolean;
}

export const getDataFromStorage = (name: string): StorageData | null => {
  const data = getSessionStorageItem(name);

  return data ? JSON.parse(data) : null;
};

export const setDataToStorage = (name: string, data: StorageData | null) => {
  setSessionStorageItem(name, JSON.stringify(data));
};

const removeDataFromStorage = (name: string) => {
  removeSessionStorageItem(name);
};

interface Props {
  name: string;
  ms: number;
  cb: () => void;
}

export const useRefreshTimer = ({ name, ms, cb }: Props) => {
  const timerId = useRef<NodeJS.Timeout | null>();

  const handleTimerExecuted = () => {
    cb();
    timerId.current = null;
    setDataToStorage(name, {
      ts: new Date(),
      isExecuted: true,
    });
  };

  const handleStartTimer = () => {
    const storageData = getDataFromStorage(name);

    if (!storageData) {
      setDataToStorage(name, {
        ts: addMilliseconds(new Date(), ms),
        isExecuted: false,
      });

      timerId.current = setTimeout(handleTimerExecuted, ms);
    } else if (!storageData.isExecuted && storageData.ts && !timerId.current) {
      const diff = differenceInMilliseconds(
        new Date(storageData.ts),
        new Date()
      );
      timerId.current = setTimeout(handleTimerExecuted, diff > 0 ? diff : 0);
    }
  };

  const handleStopTimer = () => {
    if (timerId.current) {
      clearTimeout(timerId.current);
      timerId.current = null;
    }
  };

  const handleRemoveTimer = () => {
    removeDataFromStorage(name);
  };

  return {
    handleStartTimer,
    handleStopTimer,
    handleRemoveTimer,
  };
};
